/**
 * 英語口試題庫 — 三年級口語評量(翰林,新竹安興國小 114 學年度第二學期第二次
 * 定期評量;來源照片由 boss 2026-06-09 提供)。
 *
 * 學習/遊戲分離(CLAUDE.md 紅線):純學習側資料,絕不碰任何戰鬥數值。
 * 用詞:題庫只在資料層對應科目(英語=翰林),UI 一律遊戲化/學科中性呈現。
 *
 * 結構刻意對齊紙本口試卷三大部分:
 *   Part 1 朗讀 Read aloud(15 秒念一段)
 *   Part 2 家庭介紹 Family Introduction(60 秒看家庭樹自我介紹)
 *   Part 3 問答 Ask and Answer(15 秒一問一答)
 */

/** 口試題型。 */
export type OralPartKind = 'read-aloud' | 'introduce' | 'qa';

/** 圖卡單字(Part 3 的動作 / 動物卡;emoji 當點綴,非主視覺)。 */
export interface OralVocab {
  word: string;
  emoji: string;
}

/** 單一口試題。 */
export interface OralItem {
  /** 穩定 id(測試 / 進度依賴)。 */
  id: string;
  /**
   * 學生要說出的英文(朗讀段落 / 示範答案)。也是 TTS 示範與自動發音檢查的比對
   * 目標。
   */
  text: string;
  /** 選填中文提示 / 說明。 */
  hintZh?: string;
  /** 問答題的提問句(會顯示 + TTS 念出);`text` 則是示範答案。 */
  question?: string;
}

/** 口試一個部分(對應紙本 Part 1/2/3)。 */
export interface OralPart {
  id: string;
  kind: OralPartKind;
  /** 顯示標題,例:`Part 1 朗讀 Read aloud`。 */
  title: string;
  /** 中文作答說明。 */
  instructionZh: string;
  /** 建議秒數(15 / 60),純提示用。 */
  seconds?: number;
  items: OralItem[];
  /** introduce 部分的可填空範本(逐行;`____` 為填空)。 */
  template?: string[];
  /** introduce / qa 用到的圖卡單字(看圖說)。 */
  vocab?: OralVocab[];
}

/** 一份完整口試卷。 */
export interface OralExam {
  id: string;
  title: string;
  /** 來源(科目 / 版本 / 場次),只在資料層出現。 */
  source: string;
  parts: OralPart[];
}

/** Part 3-B 動作圖卡。 */
const ACTION_VOCAB: OralVocab[] = [
  { word: 'dance', emoji: '💃' },
  { word: 'draw', emoji: '✏️' },
  { word: 'sing', emoji: '🎤' },
  { word: 'swim', emoji: '🏊' },
  { word: 'fly a kite', emoji: '🪁' },
  { word: 'ride a bike', emoji: '🚲' },
];

/** Part 3-C 動物圖卡。 */
const ANIMAL_VOCAB: OralVocab[] = [
  { word: 'bird', emoji: '🐦' },
  { word: 'cat', emoji: '🐱' },
  { word: 'dog', emoji: '🐶' },
  { word: 'frog', emoji: '🐸' },
  { word: 'rabbit', emoji: '🐰' },
  { word: 'ox', emoji: '🐂' },
];

/**
 * 三年級英語口試卷(完整一份)。Part 3 用圖卡單字展開多個練習題,讓小孩把句型
 * 套熟(Can your ... ? / Is it a/an ... ?)。
 */
export const ORAL_EXAM_G3: OralExam = {
  id: 'oral-g3-english-2nd',
  title: '英語口試練習',
  source: '英語 · 翰林 · 三年級第二學期 · 口語評量',
  parts: [
    {
      id: 'part1-read-aloud',
      kind: 'read-aloud',
      title: 'Part 1 朗讀 Read aloud',
      instructionZh: '看著句子大聲念出來,15 秒內念完一段。先按「示範」聽老師念,再自己念。',
      seconds: 15,
      items: [
        { id: 'p1-1', text: 'The vet has a pen. He can draw a hen. The hen is in a net.', hintZh: '短 e 的音:vet、pen、hen、net。' },
        { id: 'p1-2', text: 'Mr. Pig, put on the wig. Mr. Pig, sit on the kit. Give me a big smile, Mr. Pig.', hintZh: '短 i 的音:Pig、wig、sit、kit、big。' },
        { id: 'p1-3', text: 'A fox is on a box. The box is by a pot. The pot is very hot.', hintZh: '短 o 的音:fox、box、pot、hot。' },
        { id: 'p1-4', text: 'Get off the mug. Get off the bun. Little bug, little bug. Run! Run! Run!', hintZh: '短 u 的音:mug、bun、bug、run。' },
      ],
    },
    {
      id: 'part2-introduce',
      kind: 'introduce',
      title: 'Part 2 家庭介紹 Family Introduction',
      instructionZh: '看家庭樹,60 秒內用英文介紹你的家人。下面有一段示範,可以照著念,再換成你自己的家人。',
      seconds: 60,
      template: [
        'Hi! My name is ____. I am ____ years old. I am a ____.',
        "This is my father. He's a ____. This is my mother. She's a ____.",
        "This is my grandpa. He's a ____. This is my grandma. She's a ____.",
        "This is my brother. He's a ____. This is my sister. She's a ____.",
        'My ____ can ____. My ____ can ____. We are a happy family!',
      ],
      vocab: [
        { word: 'teacher', emoji: '👨‍🏫' },
        { word: 'nurse', emoji: '👩‍⚕️' },
        { word: 'doctor', emoji: '🧑‍⚕️' },
        { word: 'cook', emoji: '👨‍🍳' },
        { word: 'student', emoji: '🧑‍🎓' },
      ],
      items: [
        {
          id: 'p2-sample',
          hintZh: '示範(照家庭樹):爺爺老師、奶奶護士、爸爸醫生、媽媽廚師,自己和哥哥姐姐是學生。',
          text:
            "Hi! My name is Andy. I am nine years old. I am a student. " +
            "This is my father. He's a doctor. This is my mother. She's a cook. " +
            "This is my grandpa. He's a teacher. This is my grandma. She's a nurse. " +
            "This is my brother. He's a student. This is my sister. She's a student. " +
            'My father can swim. My mother can sing. We are a happy family!',
        },
      ],
    },
    {
      id: 'part3-qa',
      kind: 'qa',
      title: 'Part 3 問答 Ask and Answer',
      instructionZh: '聽問題,15 秒內回答完整的句子。先按「示範」聽答法。',
      seconds: 15,
      vocab: [...ACTION_VOCAB, ...ANIMAL_VOCAB],
      items: [
        ...ACTION_VOCAB.map((v, i) => ({
          id: `p3b-${i + 1}`,
          question: `Can your grandfather ${v.word}?`,
          text: `Yes, he can. / No, he can't.`,
          hintZh: `動作:${v.word}。會就說 Yes, he can.;不會就說 No, he can't.`,
        })),
        ...ANIMAL_VOCAB.map((v, i) => {
          const article = /^[aeiou]/i.test(v.word) ? 'an' : 'a';
          return {
            id: `p3c-${i + 1}`,
            question: `Is it ${article} ${v.word}?`,
            text: `Yes, it is. / No, it's not.`,
            hintZh: `動物:${v.word}。是就說 Yes, it is.;不是就說 No, it's not.`,
          };
        }),
      ],
    },
  ],
};

/** 目前提供的口試卷清單(未來可擴充多份)。 */
export const ORAL_EXAMS: OralExam[] = [ORAL_EXAM_G3];

/**
 * 一題「示範」要念出來的英文 —— 朗讀 / 介紹直接念目標句;問答念「問題 + 兩種答法」
 * (把 `Yes... / No...` 的斜線拆成兩句,TTS 才不會念出「slash」)。
 *
 * 單一真相:**前端示範 fallback 與離線預產生器(tools/gen-oral-audio.ts)都用這個
 * 函式**,確保預產的 mp3 與即時 TTS 念的內容一致(檔名 = item.id)。
 */
export function demoTextFor(part: OralPart, item: OralItem): string {
  if (part.kind === 'qa') {
    const answers = item.text.split('/').map((s) => s.trim()).filter((s) => s.length > 0);
    return `${item.question ?? ''} ${answers.join(' ')}`.trim();
  }
  return item.text;
}

/** 一題對應的離線示範音檔路徑(public/ 之下,Vite 以根目錄提供)。 */
export function oralAudioUrl(item: OralItem): string {
  return `/audio/oral/${item.id}.mp3`;
}
